/**
 * The Apply Content Language job contract, as the service actually returns it,
 * plus the pure rules for reading one.
 *
 * The types below mirror the service's responses rather than restating a plan.
 * Two distinctions in that contract are load-bearing and are preserved
 * throughout the Admin:
 *
 * - A decided operation is not an execution status. The classifier says what a
 *   vehicle needs (`translate`, `restore`, `skip-locked`, ...); the status says
 *   how that decision ended (`applied`, `planned`, `skipped`, `error`). A write
 *   that lost a race to a manual save is `skipped`, and calling it
 *   `skip-ineligible` would claim the vehicle was never a candidate.
 * - Description and highlight are counted and reported separately all the way
 *   down to the individual vehicle, because one vehicle routinely has a
 *   different outcome for each.
 *
 * No transport here: this module is types and pure functions, so the rules can
 * be tested without a store or a network.
 */
import type { ContentLanguageOperation, ResolvedContentLanguage } from './contentLanguage'

export type LanguageJobStatusValue = 'running' | 'completed' | 'failed'

/** `dry-run` classifies without writing. This slice starts `apply` jobs only. */
export type LanguageJobMode = 'apply' | 'dry-run'

export type OutcomeStatus = 'applied' | 'planned' | 'skipped' | 'error'

export type ContentType = 'description' | 'highlight'

export type OperationCounters = Record<ContentLanguageOperation, number>

export interface ContentCounters {
  operations: OperationCounters
  applied: number
  /** Would have been applied, had the job not been a dry run. */
  planned: number
  skipped: number
  errors: number
}

export interface JobCounters {
  description: ContentCounters
  highlight: ContentCounters
}

export interface JobTotals {
  applied: number
  planned: number
  skipped: number
  errors: number
}

/** Response of `GET /language-jobs/{jobId}`. */
export interface LanguageJobStatus {
  jobId: string
  accountId: string
  mode: LanguageJobMode
  descriptionLanguage: ResolvedContentLanguage
  highlightLanguage: ResolvedContentLanguage
  requestedBy: string
  requestedAt: string
  updatedAt: string
  completedAt?: string
  status: LanguageJobStatusValue
  vinsTotal: number
  vinsProcessed: number
  counters: JobCounters
  /**
   * Present only when the job itself failed. Carries a serialized execution
   * cause, so it is never rendered; the Admin reports that the job failed and
   * nothing about how.
   */
  failureReason?: string
  totals: JobTotals
  resultPages: number
}

export interface ContentOutcome {
  contentType: ContentType
  /** What the classifier decided, preserved even where execution did not apply it. */
  operation: ContentLanguageOperation
  status: OutcomeStatus
  reason: string
}

/**
 * One vehicle's outcome. The service also returns a `metaUpdate` field for its
 * own aggregation step; it is deliberately not modelled here.
 */
export interface VinResult {
  vin: string
  description: ContentOutcome
  highlight: ContentOutcome
  dryRun?: true
}

/** Response of `GET /language-jobs/{jobId}/results`. Pages are zero-based. */
export interface LanguageJobResultsPage {
  jobId: string
  page: number
  pageCount: number
  pageSize: number
  status: LanguageJobStatusValue
  mode: LanguageJobMode
  results: VinResult[]
}

/** Response of `POST /language-jobs`, returned with 202. */
export interface StartLanguageJobResponse {
  jobId: string
  mode: LanguageJobMode
  status: LanguageJobStatusValue
  descriptionLanguage: ResolvedContentLanguage
  highlightLanguage: ResolvedContentLanguage
}

const TERMINAL: ReadonlySet<LanguageJobStatusValue> = new Set<LanguageJobStatusValue>(['completed', 'failed'])

export const isTerminalStatus = (status: unknown): boolean => TERMINAL.has(status as LanguageJobStatusValue)

/** What the classifier decided for a vehicle. */
export const OPERATION_LABELS: Record<ContentLanguageOperation, string> = {
  generate: 'Generate',
  translate: 'Translate',
  restore: 'Restore',
  'skip-locked': 'Skip: locked',
  'skip-already-correct': 'Skip: already correct',
  'skip-ineligible': 'Skip: not eligible',
  error: 'Error',
}

/** What became of that decision. Kept distinct from the decision itself. */
export const OUTCOME_LABELS: Record<OutcomeStatus, string> = {
  applied: 'Applied',
  planned: 'Planned',
  skipped: 'Skipped',
  error: 'Error',
}

export const describeOperation = (operation: ContentLanguageOperation): string =>
  OPERATION_LABELS[operation] ?? operation

export const describeOutcome = (status: OutcomeStatus): string => OUTCOME_LABELS[status] ?? status

export type LanguageJobBannerColor = 'primary' | 'success' | 'info' | 'warning' | 'error'

type JobBannerSummary = Pick<LanguageJobStatus, 'status' | 'vinsProcessed' | 'vinsTotal' | 'totals'>

/** Semantic banner color, derived from the durable job summary rather than filtered VIN rows. */
export const languageJobBannerColor = (job: Pick<JobBannerSummary, 'status' | 'totals'>): LanguageJobBannerColor => {
  if (job.status === 'failed') return 'error'
  if (job.status === 'running') return 'primary'
  if (job.totals.errors > 0) return 'warning'
  return job.totals.skipped > 0 ? 'info' : 'success'
}

/** Product wording for the running or terminal status line. */
export const languageJobStatusLabel = (job: Pick<JobBannerSummary, 'status' | 'totals'>): string => {
  if (job.status === 'running') return 'Apply Content Language…'
  if (job.status === 'failed') return 'Apply Content Language failed'
  return job.totals.errors > 0
    ? 'Apply Content Language completed with errors'
    : 'Apply Content Language completed'
}

/** Progress wording. Skips count content outcomes, never unique vehicles. */
export const languageJobProgressLabel = (job: JobBannerSummary): string => {
  const progress = `${job.vinsProcessed} of ${job.vinsTotal} vehicles processed`
  if (job.status !== 'completed' || job.totals.skipped <= 0) return progress

  const item = job.totals.skipped === 1 ? 'content item' : 'content items'
  return `${progress} · ${job.totals.skipped} ${item} skipped`
}

/** Whether one completed apply result belongs in the exception-focused VIN table. */
export const isIssueVinResult = (result: VinResult): boolean =>
  result.description.status === 'skipped' ||
  result.description.status === 'error' ||
  result.highlight.status === 'skipped' ||
  result.highlight.status === 'error'

const CONTENT_OUTCOME_REASON_MESSAGES = new Map<string, string>([
  ['highlight-output-wrong-bullet-count', 'AI returned a different number of bullets than the source'],
  ['highlight-output-not-a-list', 'AI response was not a valid bullet list'],
  ['highlight-output-empty-bullet', 'AI returned a bullet list with an empty item'],
  ['highlight-output-bullet-too-long', 'AI returned a bullet that exceeded the allowed length'],
  ['highlight-source-not-a-list', 'Existing highlights were not a valid bullet list'],
  ['description-locked', 'Description is locked'],
  ['already-in-target-language', 'Content already matches the target language'],
  ['superseded-by-manual-save', 'A newer manual edit was kept'],
  ['record-changed-during-write', 'Content changed before the update could be saved'],
  ['missing-content-generation-ineligible', 'This content could not be generated'],
  ['generation-service-not-configured', 'AI service was unavailable'],
  ['generation-service-unreachable', 'AI service could not be reached'],
  ['generation-service-did-not-acknowledge', 'AI response could not be verified'],
  ['generation-service-returned-empty-content', 'AI returned no content'],
])

const GENERATION_UNREACHABLE_PREFIX = 'generation-service-unreachable:'
const GENERATION_STATUS_REASON = /^generation-service-status-\d{3}$/
const SAFE_TECHNICAL_REASON = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
const UNSAFE_INHERITED_REASON_NAMES = new Set([
  '__proto__',
  'constructor',
  'prototype',
  'toString',
  'valueOf',
  'hasOwnProperty',
])

/** Friendly copy for an affected content outcome; raw technical details never survive. */
export const describeContentOutcomeReason = (
  outcome: Pick<ContentOutcome, 'status' | 'reason'>,
): string => {
  const exact = CONTENT_OUTCOME_REASON_MESSAGES.get(outcome.reason)
  if (exact) return exact
  if (outcome.reason.startsWith(GENERATION_UNREACHABLE_PREFIX)) return 'AI service could not be reached'
  if (GENERATION_STATUS_REASON.test(outcome.reason)) return 'AI service could not complete the request'
  return outcome.status === 'skipped'
    ? 'No change was made to this content item'
    : 'This content item could not be updated'
}

/**
 * Support-facing reason code. Dynamic transport suffixes and arbitrary prose
 * are deliberately removed before the value reaches a tooltip or label.
 */
export const normalizeTechnicalReason = (reason: unknown): string | undefined => {
  if (typeof reason !== 'string') return undefined
  if (UNSAFE_INHERITED_REASON_NAMES.has(reason)) return undefined
  if (CONTENT_OUTCOME_REASON_MESSAGES.has(reason)) return reason
  if (reason.startsWith(GENERATION_UNREACHABLE_PREFIX)) return 'generation-service-unreachable'
  if (GENERATION_STATUS_REASON.test(reason)) return reason
  if (reason.length < 1 || reason.length > 128) return undefined
  return SAFE_TECHNICAL_REASON.test(reason) ? reason : undefined
}

/**
 * Descriptions skipped because they were locked.
 *
 * Read from the description counters alone: a highlight has no lock, so a
 * combined figure would imply one exists.
 */
export const lockedDescriptionSkips = (counters: JobCounters | undefined): number =>
  counters?.description?.operations?.['skip-locked'] ?? 0

export const jobProgressRatio = (job: Pick<LanguageJobStatus, 'vinsTotal' | 'vinsProcessed'> | null): number => {
  if (!job || job.vinsTotal <= 0) return 0
  return Math.min(1, job.vinsProcessed / job.vinsTotal)
}

/**
 * Why a call did not succeed, in terms the Admin acts on.
 *
 * Named here rather than in the transport module because these are the cases
 * the UI reasons about: a `forbidden` is a different situation for the user
 * than an `unavailable`, and a `conflict` is barely a failure at all. The
 * transport's job is to map HTTP onto this vocabulary and nothing else.
 */
export type LanguageJobFailureKind =
  /** 401: the session is not usable. The user must sign in again. */
  | 'unauthenticated'
  /** 403: a valid session without the role the action requires. */
  | 'forbidden'
  /** 409: a job is already running for this account. */
  | 'conflict'
  /** 404: no such job for this account. */
  | 'not-found'
  /** Anything else, including transport failures. Safe to retry. */
  | 'unavailable'

export interface LanguageJobFailure {
  kind: LanguageJobFailureKind
  /** Only ever populated from a 409, which names the job already running. */
  jobId?: string
}

/**
 * User-facing text for a failure, written here and nowhere else.
 *
 * Service responses are never shown. A failure body can carry an execution
 * cause, a rejected prompt, or an authorization detail, none of which belong on
 * screen, so the Admin says what happened in its own words and says nothing it
 * did not choose to say.
 */
export const START_FAILURE_MESSAGES: Record<LanguageJobFailureKind, string> = {
  unauthenticated: 'Your session has expired. Sign in again to apply a content language.',
  forbidden: 'You do not have permission to apply a content language for this account.',
  conflict: 'A content language job is already running for this account.',
  'not-found': 'Could not start the job. Try again.',
  unavailable: 'Could not start the job. Try again.',
}

/**
 * A failed refresh is not a failed job, and the wording keeps that straight:
 * these say that the progress could not be read, never that the work stopped.
 */
export const REFRESH_FAILURE_MESSAGES: Record<LanguageJobFailureKind, string> = {
  unauthenticated: 'Sign in again to see the latest progress.',
  forbidden: 'You do not have permission to view this job.',
  conflict: 'Could not refresh progress. Retrying.',
  'not-found': 'This job is no longer available.',
  unavailable: 'Could not refresh progress. Retrying.',
}

export const describeStartFailure = (failure: LanguageJobFailure | null | undefined): string =>
  failure ? START_FAILURE_MESSAGES[failure.kind] ?? START_FAILURE_MESSAGES.unavailable : ''

export const describeRefreshFailure = (failure: LanguageJobFailure | null | undefined): string =>
  failure ? REFRESH_FAILURE_MESSAGES[failure.kind] ?? REFRESH_FAILURE_MESSAGES.unavailable : ''
